// src/stores/base/api.ts
//
// Thin Axios wrapper around API Integrations v4 (int/v4/{controller}).
// Auth model is unchanged from v2: a single "X-Branches-Key" header scopes
// every request to one integration branch (see Backend/Musa3edSystem
// CLAUDE.md, "Integration v4 authentication" and follow-on sections) - v4
// did not rename or remove this header, so no auth-flow change was needed
// here beyond pointing the base URL at /int/v4/.
import axios, { AxiosInstance } from "axios";
import { App } from "vue";

export interface ApiRequestOptions {
  /**
   * Sent as the "Idempotency-Key" header. Required by v4's guest
   * recruitment-request creation endpoints (EmploymentRequest/AddFromGuest,
   * EmploymentRequest/AddWithSelectWorker) so that a retried or
   * double-clicked submission is not applied twice server-side. v2 had no
   * such mechanism - this is a genuine, breaking contract addition in v4,
   * not a cosmetic rename.
   */
  idempotencyKey?: string;
}

class ApiService {
  private static axiosInstance: AxiosInstance;
  private static branchKey: string;

  public static init(app: App<Element>, baseURL: string, branchKey: string): void {
    if (!baseURL) {
      throw new Error(
        "VUE_APP_API_BASE_URL is not configured. Set it in .env / .env.local before starting the app."
      );
    }
    if (!branchKey) {
      throw new Error(
        "VUE_APP_INTEGRATION_BRANCH_KEY is not configured. Set it in .env / .env.local before starting the app."
      );
    }

    ApiService.branchKey = branchKey;
    ApiService.axiosInstance = axios.create({
      baseURL,
      headers: ApiService.buildHeaders(branchKey),
    });

    app.config.globalProperties.$http = ApiService.axiosInstance;
  }

  private static buildHeaders(branchKey: string): Record<string, string> {
    return {
      "X-Branches-Key": branchKey,
      Culture: "ar",
      "Content-Type": "application/json",
      // Was "application/vnd.hal+json" under the previous integration
      // version. The current integration returns plain JSON (BaseResult<T>)
      // just like the previous one did, never HAL - the old Accept header
      // simply did not describe what the API actually serves.
      Accept: "application/json",
    };
  }

  private static setHeader(): void {
    Object.assign(ApiService.axiosInstance.defaults.headers.common, {
      "X-Branches-Key": ApiService.branchKey,
    });
  }

  public static async get<T = unknown>(
    url: string,
    params?: object,
    signal?: AbortSignal
  ): Promise<{ data: T }> {
    ApiService.setHeader();
    const response = await ApiService.axiosInstance.get<T>(url, { params, signal });
    return { data: response.data };
  }

  public static async post<T = unknown>(
    url: string,
    data?: object,
    options?: ApiRequestOptions
  ): Promise<{ data: T }> {
    ApiService.setHeader();
    const response = await ApiService.axiosInstance.post<T>(url, data, {
      headers: options?.idempotencyKey
        ? { "Idempotency-Key": options.idempotencyKey }
        : undefined,
    });
    return { data: response.data };
  }
}
export default ApiService;
